// Note creation + AI generation pipeline for the web.
// Mirrors the iOS flow in Wellnotes/Utilities/NoteGenerationManager.swift and
// Wellnotes/Views/SubViews/NoteView/GenerateNote.swift:
//   1. Reserve a noteID + write a placeholder doc (`isLoading: true`).
//   2. Optionally upload attached files to Firebase Storage.
//   3. POST to `generateNote` with an SSE accept header and stream tokens —
//      saying, as the apps do, that this page shows interactive widgets
//      (utils/widgetCapability.js).
//   4. Apply OVERWRITE replacements + flush content updates to Firestore.
//   5. On the final delta, write `isLoading: false` and the final content.
//
// Exposes a small reactive `noteCreationState` so UI surfaces (FAB, modal,
// note detail) can show "generating…" feedback while the stream runs.

import { reactive } from "vue";
import {
  doc,
  collection,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, storage } from "../firebase";
import { authState } from "./authStore";
import { widgetCapability, WIDGETS_HEADER } from "../utils/widgetCapability";

const GENERATE_NOTE_ENDPOINT = "https://generatenote-454hdhzgfa-uc.a.run.app";

export const noteCreationState = reactive({
  // Map of noteID → { content, isComplete, error }
  generations: {},
});

function setGenerationState(noteID, partial) {
  const prev = noteCreationState.generations[noteID] || {
    content: "",
    isComplete: false,
    error: null,
  };
  noteCreationState.generations[noteID] = { ...prev, ...partial };
}

function clearGenerationState(noteID) {
  delete noteCreationState.generations[noteID];
}

function reserveNoteID(uid) {
  return doc(collection(db, "users", uid, "notes")).id;
}

function ensureAuth() {
  const user = authState.currentUser;
  if (!user) throw new Error("not-authenticated");
  return user;
}

async function getIdToken() {
  const user = ensureAuth();
  if (typeof user.getIdToken !== "function") throw new Error("invalid-auth-user");
  return user.getIdToken();
}

async function writePlaceholderNote({ uid, noteID, noteType, folder, fileURLs }) {
  const ref = doc(db, "users", uid, "notes", noteID);
  await setDoc(
    ref,
    {
      id: noteID,
      content: "",
      noteType,
      folder: folder || "",
      isLoading: true,
      deleted: false,
      fileURLs: fileURLs || [],
      timestamp: serverTimestamp(),
    },
    { merge: true },
  );
}

// What Storage accepts in a note's folder (server/storage.rules): a file
// outside it is refused with a bare permission error, after the note was
// already placed in the list — so it is checked here first.
const NOTE_MAX_BYTES = 25 * 1024 * 1024;
const NOTE_ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/gif", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain", "text/markdown", "text/csv", "application/rtf",
]);
// Browsers leave `type` empty for some of these: the extension says it.
const TYPE_BY_EXTENSION = {
  pdf: "application/pdf", txt: "text/plain", md: "text/markdown", markdown: "text/markdown",
  csv: "text/csv", rtf: "application/rtf", heic: "image/heic", heif: "image/heif",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

function contentTypeOf(file) {
  const ext = (file.name || "").split(".").pop().toLowerCase();
  return file.type || TYPE_BY_EXTENSION[ext] || "";
}

/** An error the form can explain: `code` and the file it is about. */
function fileError(code, file) {
  const err = new Error(code);
  err.code = code;
  err.fileName = file.name || "";
  return err;
}

function checkUploadable(files) {
  for (const file of files || []) {
    if (!NOTE_ALLOWED_TYPES.has(contentTypeOf(file))) throw fileError("unsupported-file", file);
    if (file.size > NOTE_MAX_BYTES) throw fileError("file-too-large", file);
  }
}

async function uploadFiles(uid, noteID, files) {
  if (!files || files.length === 0) return [];
  const uploaded = [];
  for (const file of files) {
    const safeName = (file.name || "file").replace(/[^A-Za-z0-9._-]/g, "_");
    const path = `users/${uid}/notes/${noteID}/${Date.now()}_${safeName}`;
    const ref = storageRef(storage, path);
    const contentType = contentTypeOf(file);
    await uploadBytes(ref, file, { contentType: contentType || undefined });
    const url = await getDownloadURL(ref);
    uploaded.push({
      fileName: file.name || safeName,
      fileURL: url,
      mimeType: contentType || null,
    });
  }
  return uploaded;
}

/**
 * Runs the generateNote SSE stream. Tokens come in as `data: <token>` lines;
 * we accumulate them, periodically flush to Firestore, handle [OVERWRITE:name]
 * placeholder substitutions, and stop on `[DONE]` / `[ERROR]`.
 */
async function streamGeneration({
  endpoint,
  payload,
  authToken,
  onProgress,
  onComplete,
  onError,
}) {
  const baseHeaders = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    Authorization: `Bearer ${authToken}`,
  };
  const headers = await widgetCapability.headersFor(endpoint, baseHeaders);
  let response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // An endpoint deployed before it allowed the widgets header refuses the
    // whole request in the browser (CORS), whatever the probe said: the
    // note is generated without widgets rather than not at all.
    if (!(WIDGETS_HEADER in headers)) throw error;
    console.warn("generateNote refused the widgets header; retrying without it", error);
    response = await fetch(endpoint, {
      method: "POST",
      headers: baseHeaders,
      body: JSON.stringify(payload),
    });
  }
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    throw new Error(`generateNote HTTP ${response.status}: ${text || "no body"}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let regularText = "";
  let pending = "";
  let lastFlush = Date.now();
  const placeholders = {};
  let currentEvent = null;
  let doneReceived = false;

  const applyPlaceholders = (text) => {
    if (!text) return text;
    let out = text;
    for (const [key, value] of Object.entries(placeholders)) {
      out = out.split(key).join(value);
    }
    return out;
  };

  const emit = (final = false) => {
    const combined = regularText + pending;
    onProgress(combined, final);
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split(/\r?\n/);
    buf = lines.pop() || "";
    for (const rawLine of lines) {
      const line = rawLine;
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
        continue;
      }
      if (!line.startsWith("data: ")) continue;
      const dataString = line.slice(6).replace(/\\n/g, "\n");

      const looksLikeUsage =
        dataString.includes('"note_input_tokens"') ||
        dataString.includes('"note_output_tokens"') ||
        dataString.includes('"input_tokens"') ||
        dataString.includes('"output_tokens"');
      if (currentEvent === "usage" || looksLikeUsage) {
        currentEvent = null;
        continue;
      }

      const token = dataString;
      if (token === "[DONE]") {
        doneReceived = true;
        continue;
      }
      if (token.startsWith("[ERROR]")) {
        onError(token);
        return;
      }
      if (token === "[LOADING_NOTE]") continue;
      if (token.startsWith("[OVERWRITE:")) {
        const closing = token.indexOf("]");
        if (closing < 0) continue;
        const name = token.slice("[OVERWRITE:".length, closing).trim();
        const replacement = token.slice(closing + 1);
        placeholders[`[${name}]`] = replacement;
        regularText = applyPlaceholders(regularText);
        pending = applyPlaceholders(pending);
        emit(false);
        continue;
      }
      if (doneReceived) continue;
      pending += token;
      const now = Date.now();
      if (now - lastFlush >= 250) {
        regularText += pending;
        pending = "";
        lastFlush = now;
        emit(false);
      }
    }
  }
  if (pending) {
    regularText += pending;
    pending = "";
  }
  emit(true);
  onComplete(regularText);
}

/**
 * Main entry point. Creates a placeholder note, uploads attachments, kicks
 * off the SSE stream, and resolves with the final noteID once generation
 * completes (or rejects on stream error).
 */
export async function createGeneratedNote({
  noteType,
  folder = "",
  context = "",
  files = [],
  maxPages,
}) {
  const user = ensureAuth();
  const uid = user.uid;
  // Before anything is written: a file Storage would refuse is said now,
  // not after an empty note has appeared in the list.
  checkUploadable(files);
  const noteID = reserveNoteID(uid);
  // Asked while the note is set up, so the answer is in by the request.
  widgetCapability.accepts(GENERATE_NOTE_ENDPOINT);

  setGenerationState(noteID, { content: "", isComplete: false, error: null });

  // 1) Reserve the note doc immediately so it appears in the list.
  await writePlaceholderNote({ uid, noteID, noteType, folder, fileURLs: [] });

  // 2) Upload files (if any) and patch the note doc with their URLs.
  let uploaded = [];
  try {
    uploaded = await uploadFiles(uid, noteID, files);
    if (uploaded.length > 0) {
      await updateDoc(doc(db, "users", uid, "notes", noteID), {
        fileURLs: uploaded.map((f) => f.fileURL),
      });
    }
  } catch (err) {
    setGenerationState(noteID, { error: err.message || "upload-failed" });
    // Nothing will ever fill it: the placeholder goes rather than staying
    // in the list empty and loading for good.
    await deleteDoc(doc(db, "users", uid, "notes", noteID)).catch(() => {});
    clearGenerationState(noteID);
    if (!err.code || !String(err.code).startsWith("storage/")) throw err;
    throw Object.assign(new Error("upload-failed"), { code: "upload-failed" });
  }

  // 3) Kick off the streaming generation in the background. The caller
  //    can navigate to the note immediately — content will stream in.
  const authToken = await getIdToken();
  const userContext =
    context || (uploaded.length > 0 ? `files(${uploaded.length})` : context);
  const payload = {
    userID: uid,
    userContext,
    noteID,
    noteType,
    folder: folder || "",
    isSandbox: false,
    messages: {
      noteGeneration: "Note generation",
      initializing: "Initializing",
      analyzing: "Analyzing",
      generating: "Generating",
      generatingExercises: "Generating exercises",
      quizzesAndExercises: "Quizzes and exercises",
      complete: "Complete",
      error: "Error",
      errorOccurred: "An error occurred",
    },
  };
  if (maxPages != null) payload.maxPages = maxPages;
  if (uploaded.length > 0) payload.fileURLs = uploaded;

  // Fire-and-forget — caller returns immediately; UI listens via state.
  // NOTE: We deliberately do NOT write the streaming tokens to Firestore
  // from the client. The `generateNote` Cloud Function already persists the
  // final content + clears `isLoading` once the stream completes (see
  // Server/functions/note.js:1029 and :1204). Doing per-token writes from
  // the client would double the work and run up Firestore costs for no
  // visible gain — the in-memory `noteCreationState.generations` already
  // drives the live editor view via NoteDetail's watcher.
  (async () => {
    try {
      await streamGeneration({
        endpoint: GENERATE_NOTE_ENDPOINT,
        payload,
        authToken,
        onProgress: (content, isFinal) => {
          setGenerationState(noteID, { content, isComplete: isFinal });
        },
        onComplete: (finalContent) => {
          setGenerationState(noteID, {
            content: finalContent,
            isComplete: true,
          });
          setTimeout(() => clearGenerationState(noteID), 60_000);
        },
        onError: (message) => {
          setGenerationState(noteID, { error: message, isComplete: true });
          // Stream errored before the back-end could clear `isLoading` — make
          // sure the note doesn't spin forever in the UI. (Best-effort; the
          // back-end usually owns this flag.)
          updateDoc(doc(db, "users", uid, "notes", noteID), {
            isLoading: false,
          }).catch(() => {});
        },
      });
    } catch (err) {
      console.error("streamGeneration failed:", err);
      setGenerationState(noteID, {
        error: err.message || "stream-failed",
        isComplete: true,
      });
      updateDoc(doc(db, "users", uid, "notes", noteID), {
        isLoading: false,
      }).catch(() => {});
    }
  })();

  return noteID;
}

/**
 * Creates a blank "simple note" with no AI generation — just an empty doc
 * the user can immediately edit. Used by the FAB's "Simple note" option.
 */
export async function createSimpleNote({ folder = "" } = {}) {
  const user = ensureAuth();
  const uid = user.uid;
  const noteID = reserveNoteID(uid);
  await setDoc(doc(db, "users", uid, "notes", noteID), {
    id: noteID,
    content: "",
    noteType: "CLASSIC",
    folder: folder || "",
    isLoading: false,
    deleted: false,
    timestamp: serverTimestamp(),
  });
  return noteID;
}
